import type { AccountCoupon } from '@/data/Content/AccountCoupons';
import type { DppCouponSource } from '@/data/dpp/types';

const ACTIVATED_STORAGE_PREFIX = 'dpp-activated-coupons:';
const PENDING_STORAGE_PREFIX = 'dpp-pending-coupons:';

export const GUEST_REPURCHASE_COUPON_KEY = 'dpp-repurchase-coupon-guest';

const isExpiredCoupon = ({ expirationDateTime }: AccountCoupon) =>
	!!expirationDateTime && Date.parse(expirationDateTime) <= Date.now();

const activatedStorageKey = (userId: string) => `${ACTIVATED_STORAGE_PREFIX}${userId}`;

const readActivatedCoupons = (userId: string): AccountCoupon[] => {
	try {
		const raw = globalThis.localStorage?.getItem(activatedStorageKey(userId));
		const parsed = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

const writeActivatedCoupons = (userId: string, coupons: AccountCoupon[]) => {
	try {
		if (coupons.length) {
			globalThis.localStorage?.setItem(activatedStorageKey(userId), JSON.stringify(coupons));
		} else {
			globalThis.localStorage?.removeItem(activatedStorageKey(userId));
		}
	} catch {
		return;
	}
};

export const rememberActivatedCoupon = (userId: string, coupon: AccountCoupon) => {
	const others = readActivatedCoupons(userId).filter(({ id }) => id !== coupon.id);
	writeActivatedCoupons(userId, [...others, { ...coupon, status: 'APPLIED' }]);
};

export const forgetActivatedCoupon = (userId: string, couponId: string) =>
	writeActivatedCoupons(
		userId,
		readActivatedCoupons(userId).filter(({ id }) => id !== couponId)
	);

const pendingStorageKey = (userId: string) => `${PENDING_STORAGE_PREFIX}${userId}`;

export type PendingCoupon = { id: string; source?: DppCouponSource };

const toPendingCoupon = (item: unknown): PendingCoupon | undefined => {
	if (typeof item === 'string') {
		return { id: item };
	}
	const { id, source } = (item ?? {}) as Partial<PendingCoupon>;
	return typeof id === 'string' ? { id, ...(source ? { source } : {}) } : undefined;
};

export const readPendingCoupons = (userId: string): PendingCoupon[] => {
	try {
		const raw = globalThis.localStorage?.getItem(pendingStorageKey(userId));
		const parsed = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed)
			? parsed.map(toPendingCoupon).filter((coupon): coupon is PendingCoupon => !!coupon)
			: [];
	} catch {
		return [];
	}
};

const writePendingCoupons = (userId: string, coupons: PendingCoupon[]) => {
	try {
		if (coupons.length) {
			globalThis.localStorage?.setItem(pendingStorageKey(userId), JSON.stringify(coupons));
		} else {
			globalThis.localStorage?.removeItem(pendingStorageKey(userId));
		}
	} catch {
		return;
	}
};

export const markCouponPending = (userId: string, couponId: string, source?: DppCouponSource) => {
	const others = readPendingCoupons(userId).filter(({ id }) => id !== couponId);
	writePendingCoupons(userId, [...others, { id: couponId, ...(source ? { source } : {}) }]);
};

export const clearCouponPending = (userId: string, couponId: string) =>
	writePendingCoupons(
		userId,
		readPendingCoupons(userId).filter(({ id }) => id !== couponId)
	);

export const withActivatedCoupons = (userId: string, coupons: AccountCoupon[]): AccountCoupon[] => {
	const activated = readActivatedCoupons(userId).filter((coupon) => !isExpiredCoupon(coupon));
	writeActivatedCoupons(userId, activated);
	const activatedIds = new Set(activated.map(({ id }) => id));
	const listedIds = new Set(coupons.map(({ id }) => id));
	return [
		...coupons.map((coupon) =>
			activatedIds.has(coupon.id) ? { ...coupon, status: 'APPLIED' as const } : coupon
		),
		...activated.filter(({ id }) => !listedIds.has(id)),
	];
};

export const clearRepurchaseCouponStorage = () => {
	try {
		globalThis.sessionStorage?.removeItem(GUEST_REPURCHASE_COUPON_KEY);
		const storage = globalThis.localStorage;
		if (!storage) {
			return;
		}
		Object.keys(storage)
			.filter(
				(key) => key.startsWith(ACTIVATED_STORAGE_PREFIX) || key.startsWith(PENDING_STORAGE_PREFIX)
			)
			.forEach((key) => storage.removeItem(key));
	} catch {
		return;
	}
};
